import React, { useState, useEffect, Suspense } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { Sidebar } from "./Sidebar"
import { Header } from "./Header"
import { ChatWidget } from "./ChatWidget"
import { ActivityTracker } from "../../features/activity"
import { TimerIndicator } from "../../features/time-tracking/components/TimerIndicator"
import { TimeEntryForm } from "../../features/time-tracking/components/TimeEntryForm"
import { TimerStartDialog } from "../../features/time-tracking/components/TimerStartDialog"
import { useTimerStore } from "../../features/time-tracking/stores/timer-store"
import { useUIStore } from "../../stores"
import { cn } from "../../lib/utils"
import { NoDivisionSelected } from "./NoDivisionSelected"

export function DashboardLayout() {
  const { sidebarCollapsed, mobileMenuOpen, setMobileMenuOpen, selectedDivision } = useUIStore()
  const {
    stoppedTimerData,
    setStoppedTimerData,
    timerStartDialogOpen,
    setTimerStartDialogOpen,
  } = useTimerStore()
  const location = useLocation()

  // Local state so the form stays open after stoppedTimerData is consumed by the form's useEffect
  const [timerEntryFormOpen, setTimerEntryFormOpen] = useState(false)
  useEffect(() => {
    if (stoppedTimerData) setTimerEntryFormOpen(true)
  }, [stoppedTimerData])

  // Check if we're on a page that needs full-height layout (no padding)
  // Match when the segment after /matters/ is a MongoDB ObjectID (24 hex) or numeric ID
  const isMatterDetail = /^\/matters\/([a-f0-9]{24}|\d+)(\/|$)/i.test(location.pathname)
  const isTextsPage = location.pathname.startsWith("/texts")
  const isTrainingCourse = location.pathname.startsWith("/training/course")
  const isFullHeightPage = isMatterDetail || isTextsPage || isTrainingCourse

  return (
    <div className="flex h-screen overflow-hidden bg-muted">
      {/* Activity Tracker - monitors user interactions */}
      <ActivityTracker />

      {/* Mobile/Tablet backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[199] lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar />

      {/* Main content area */}
      <div
        className={cn(
          "flex flex-1 flex-col overflow-hidden transition-all duration-300",
          // Mobile/Tablet: no margin (sidebar overlays)
          "ml-0",
          // Desktop: respect collapse state
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-[200px]"
        )}
      >
        {/* Header */}
        <Header />

        {/* Page content */}
        <main className={cn("flex-1 overflow-auto bg-muted relative", !isFullHeightPage && "p-4", !isFullHeightPage && "pb-24")}>
          {selectedDivision ? (
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          ) : (
            <NoDivisionSelected />
          )}
        </main>
      </div>

      {/* Chat widget */}
      <ChatWidget />

      {/* Timer floating indicator */}
      <TimerIndicator />

      {/* Timer start dialog (opened globally via store) */}
      <TimerStartDialog
        open={timerStartDialogOpen}
        onOpenChange={setTimerStartDialogOpen}
      />

      {/* Time entry form after timer stop */}
      <TimeEntryForm
        open={timerEntryFormOpen}
        onOpenChange={(open) => {
          setTimerEntryFormOpen(open)
          if (!open) setStoppedTimerData(null)
        }}
      />
    </div>
  )
}
